/*
 * Copyright (C) 2021 to Present Applause App Quality, Inc. All rights reserved.
 */

import { ConnectedPosition } from '@angular/cdk/overlay';
import { AfterViewInit, Attribute, ChangeDetectionStrategy, Component, ContentChildren, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewContainerRef } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { filter, map, share } from 'rxjs/operators';
import { ResponsivePillListBoundTemplate } from './responsive-pill-list-bound-template';
import { ResponsivePillListService } from './responsive-pill-list.service';
import { ResponsivePillContext, ResponsivePillDirective } from './responsive-pill.directive';
import { coerceBooleanProperty } from "@angular/cdk/coercion";


@Component({
    selector: 'ngf-responsive-pill-list',
    templateUrl: './responsive-pill-list.component.html',
    styleUrls: ['./responsive-pill-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        ResponsivePillListService
    ],
    standalone: false
})
export class ResponsivePillListComponent implements OnInit, OnChanges, AfterViewInit {

  @Input()
  showClearAll = false;

  @Input()
  showChevron = false;

  @Input()
  minPills = 0;

  @Input()
  maxPills = Infinity;

  /**
   * Passed through to underlying ngf-pill-list.
   */
  @Input()
  positions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      offsetY: -8
    },
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 8
    }
  ];

  @Output()
  openChanged = new EventEmitter<boolean>();

  @Output()
  clearAll = new EventEmitter<void>();

  @ViewChild('pillList', {read: ViewContainerRef})
  hostViewContainer: ViewContainerRef;

  @ContentChildren(ResponsivePillDirective)
  responsivePills: QueryList<ResponsivePillDirective>;

  displayedTemplates$: Observable<ResponsivePillListBoundTemplate[]>;
  collapsedTemplates$: Observable<ResponsivePillListBoundTemplate[]>;

  readonly large: boolean;

  constructor(private elementRef: ElementRef, private service: ResponsivePillListService, @Attribute('large') large: string) {
    this.large = coerceBooleanProperty(large);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('minPills') && this.minPills < 0) {
      throw new Error('[ResponsivePillListComponent] minPills must be greater than zero');
    }

    if (changes.hasOwnProperty('maxPills') && this.maxPills < 0) {
      throw new Error('[ResponsivePillListComponent] maxPills must be greater than zero');
    }

    if ((changes.hasOwnProperty('minPills') || changes.hasOwnProperty('maxPills')) && this.maxPills < this.minPills) {
      throw new Error('[ResponsivePillListComponent] maxPills must be greater than minPills');
    }

    if (changes.hasOwnProperty('minPills')) {
      this.service.minPills = this.minPills;
    }

    if (changes.hasOwnProperty('maxPills')) {
      this.service.maxPills = this.maxPills;
    }
  }

  ngOnInit(): void {
    const slicedTemplates$ = this.service.currentBreakpoint$.pipe(
      filter((breakpoint) => breakpoint !== null),
      map((breakpoint) => {
        const displayed: ResponsivePillListBoundTemplate[] = [];
        const collapsed: ResponsivePillListBoundTemplate[] = [];
        const responsivePills = this.responsivePills;

        const firstCollapsedPillIndex = Math.min(breakpoint.index, responsivePills.length);

        for (let i = 0; i < firstCollapsedPillIndex; i++) {
          displayed.push({
            templateRef: responsivePills.get(i).templateRef,
            context: new ResponsivePillContext({collapsed: false})
          });
        }

        for (let i = firstCollapsedPillIndex; i < responsivePills.length; i++) {
          collapsed.push({
            templateRef: responsivePills.get(i).templateRef,
            context: new ResponsivePillContext({collapsed: true})
          });
        }
        return {
          displayed,
          collapsed
        };
      }),
      share({
        connector: () => new ReplaySubject(1),
        resetOnRefCountZero: true
      })
    );
    this.displayedTemplates$ = slicedTemplates$.pipe(
      map(({displayed}) => displayed)
    );
    this.collapsedTemplates$ = slicedTemplates$.pipe(
      map(({collapsed}) => collapsed),
    );
  }

  ngAfterViewInit(): void {
    // register component service only when all initialized
    this.service.register(this.hostViewContainer, this.elementRef, this.responsivePills, this.minPills, this.maxPills);
  }
}
